<div class="sel-tray">
  <div class="tray-toggle" [class.compact]="collapsed()" (click)="toggleCollapse()">
    <span class="tray-toggle-label">BASKET</span>
    <span class="tray-toggle-badges">
      @if (sel.productCount() > 0) {
        <span class="tray-badge tb-prod">P {{ sel.productCount() }}</span>
      }
      @if (sel.benefitCount() > 0) {
        <span class="tray-badge tb-ben">B {{ sel.benefitCount() }}</span>
      }
    </span>
    <span class="tray-toggle-chev">{{ collapsed() ? '▸' : '▾' }}</span>
  </div>

  @if (!collapsed()) {
    <div class="tray-body">
      <div class="tray-hdr">
        <span class="tray-count-label">
          {{ sel.productCount() }} / {{ sel.MAX_PRODUCT_SELECTIONS }} products
          @if (sel.benefitCount() > 0) {
            · {{ sel.benefitCount() }} / {{ sel.MAX_BENEFIT_SELECTIONS }} riders
          }
        </span>
        @if (hasSelections()) {
          <span class="tray-clear" (click)="clearAll()">Clear all</span>
        }
      </div>

      <div class="tray-list">
        @for (p of sel.products(); track p.selectionId) {
          @let inReport = isInReport(p);
          <div class="tray-card"
               [class.drag-suggest]="onProductSelectionStep() && !inReport"
               [class.in-report]="inReport"
               [attr.draggable]="inReport ? 'false' : 'true'"
               (dragstart)="onProductDragStart($event, p)"
               (dragend)="onProductDragEnd()">
            <span class="tray-card-handle" aria-hidden="true"
                  [title]="inReport ? 'Already in the report' : 'Drag into Product Selection'">⠿</span>
            <div class="tray-card-body">
              <div class="tray-card-top">
                <span class="tbadge" [ngClass]="getBadgeClass(p.productType)">{{ p.productType | uppercase }}</span>
                <span class="tray-card-acts">
                  <!-- Disclosures only: the one page with somewhere to drop. Pressing
                       it points at the drop boxes; dragging the card (this chip
                       included) is what actually adds the product. -->
                  @if (inReport) {
                    <span class="tray-in-report-chip"
                          title="Already in the disclosure report — remove it there to drag it again">✓ In report</span>
                  } @else if (onDisclosures()) {
                    <button type="button" class="tray-drag-chip"
                            [class.pulse]="onProductSelectionStep()"
                            [disabled]="!canJumpToProductSelection()"
                            [title]="dragButtonTip()"
                            [attr.aria-label]="'Drag ' + p.name + ' into the disclosure report'"
                            (click)="suggestDrag(); $event.stopPropagation()">
                      <span aria-hidden="true">⠿</span> Drag
                    </button>
                  }
                  <button type="button" class="tray-row-x bn-x-btn bn-x-btn--dark"
                          (click)="removeProduct(p.selectionId); $event.stopPropagation()"
                          title="Remove from basket" [attr.aria-label]="'Remove ' + p.name + ' from basket'">
                    <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
                      <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                    </svg>
                  </button>
                </span>
              </div>
              <div class="tray-card-name" (click)="openProfile(p); $event.stopPropagation()" title="View product profile">{{ p.name }}</div>
              <div class="tray-card-carrier">{{ p.carrier }}</div>
              @if (p.rateRow?.strategyName || p.rateRow?.rateTerm) {
                <div class="tray-row-sub">{{ p.rateRow?.strategyName || (p.rateRow?.rateTerm + 'yr') }}</div>
              }
            </div>

            <!-- Hover detail card — name/carrier already shown on the card
                 itself now, so this only adds what isn't: where it came from
                 and the profile link. -->
            <div class="hover-card">
              <div class="hc-contexts">
                @for (ctx of p.contexts; track $index) {
                  <div class="hc-ctx-item">
                    @if (p.contexts.length > 1) {
                      <div class="hc-ctx-num">{{ $index + 1 }}</div>
                    }
                    <div class="hc-ctx-body">
                      <div class="hc-ctx-source">{{ formatSource(ctx.source) }}</div>
                      @if (ctx.filters) {
                        <div class="hc-pills">
                          @for (pill of splitFilters(ctx.filters); track pill) {
                            <span class="hc-pill">{{ pill }}</span>
                          }
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
              <div class="hc-divider"></div>
              <span class="hc-profile" (click)="openProfile(p); $event.stopPropagation()">View product profile</span>
            </div>
          </div>
        }

        <!-- Riders added from the Benefit Calculator. The service has always
             stored these; the bar just never rendered them, so a calculator
             "+ Basket" looked like it did nothing. -->
        @for (b of sel.benefits(); track b.id) {
          <div class="tray-row">
            <span class="tbadge" [ngClass]="getBadgeClass(b.benefitType)">{{ b.benefitType | uppercase }}</span>
            <span class="tray-row-name" [title]="b.riderName">{{ b.riderName }}</span>
            <span class="tray-row-sub">{{ benefitSubtitle(b) }}</span>
            <button type="button" class="tray-row-x bn-x-btn bn-x-btn--dark"
                    (click)="removeBenefit(b.id); $event.stopPropagation()"
                    title="Remove from basket" [attr.aria-label]="'Remove ' + b.riderName + ' from basket'">
              <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>

            <div class="hover-card">
              <div class="hc-name">{{ b.riderName }}</div>
              <div class="hc-carrier">{{ b.productName }} — {{ b.carrier }}</div>
              <div class="hc-contexts">
                @for (ctx of b.contexts; track $index) {
                  <div class="hc-ctx-item">
                    <div class="hc-ctx-body">
                      <div class="hc-ctx-source">{{ formatSource(ctx.source) }}</div>
                      @if (ctx.filters) {
                        <div class="hc-pills">
                          @for (pill of splitFilters(ctx.filters); track pill) {
                            <span class="hc-pill">{{ pill }}</span>
                          }
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>

      @if (!hasSelections()) {
        <div class="tray-empty">Select products from any grid to compare or create a disclosure report</div>
      }
      @if (sel.productCount() > 0) {
        @if (onDisclosures()) {
          <button type="button" class="tray-drag-cta"
                  [class.pulse]="onProductSelectionStep() && draggableCount() > 0"
                  [disabled]="!canJumpToProductSelection() || draggableCount() === 0"
                  [title]="onProductSelectionStep() ? 'Show where to drop products' : dragButtonTip()"
                  (click)="suggestDrag()">
            <span class="tray-drag-cta-ico" aria-hidden="true">⠿</span>
            @if (onProductSelectionStep()) {
              Drag products into the report
            } @else {
              Go to Product Selection to drag →
            }
          </button>
          <div class="tray-hint">
            @if (draggableCount() === 0) {
              All basket products are already in the report.
            } @else if (onProductSelectionStep()) {
              Drag a card onto a highlighted box in the report to add it.
            } @else if (canJumpToProductSelection()) {
              Products are added on <strong>Product Selection</strong>.
            } @else {
              Fix the client / advisor name on <strong>Information</strong> to continue.
            }
          </div>
        } @else {
          <div class="tray-hint">
            Go to <strong>Product Selection</strong> to drag these into the report.
          </div>
        }
        <!-- Hidden while a comparison is already open: offering "Compare
             products" from inside the comparison asks the user to open what
             they are looking at. Not relabelled to "Update comparison" because
             it would not update anything -- the comparison page reads its
             products once from the staged context and does not track the
             basket, so the button would lie. Closing and comparing again is
             the honest path, and it is one click. -->
        @if (!compareNav.isComparing()) {
          <div class="tray-actions">
            <button class="tray-btn tray-btn-cmp"
                    [disabled]="!canCompare()" [title]="compareTip()"
                    (click)="compareProducts()">Compare products</button>
            <!-- <button class="tray-btn tray-btn-dis" (click)="createDisclosure()">Disclosure</button> -->
          </div>
        }
      }
    </div>
  }
</div>
